/* TCG Zone: hero hand, card tilt, rail controls and one-time reveals. */

const tcgzFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const tcgzReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Eases a value toward its target each frame, so pointer-driven motion has weight instead of snapping. */
function tcgzFollow(apply, stiffness = 0.12) {
  const current = { x: 0, y: 0 };
  const target = { x: 0, y: 0 };
  let frame = null;
  const tick = () => {
    current.x += (target.x - current.x) * stiffness;
    current.y += (target.y - current.y) * stiffness;
    apply(current.x, current.y);
    const settled = Math.abs(target.x - current.x) < 0.001 && Math.abs(target.y - current.y) < 0.001;
    frame = settled ? null : requestAnimationFrame(tick);
  };
  return (x, y) => {
    target.x = x;
    target.y = y;
    if (!frame) frame = requestAnimationFrame(tick);
  };
}

/* ---------- Hero hand ---------- */

if (!customElements.get('tcgz-hand')) {
  customElements.define(
    'tcgz-hand',
    class TcgzHand extends HTMLElement {
      connectedCallback() {
        this.hero = this.closest('.tcgz-hero');
        // The deal plays once, after images have had a moment to decode.
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-dealt')));

        if (!this.hero || !tcgzFinePointer.matches || tcgzReducedMotion.matches) return;

        const move = tcgzFollow((x, y) => {
          this.style.setProperty('--tilt-x', `${(-y * 8).toFixed(2)}deg`);
          this.style.setProperty('--tilt-y', `${(x * 10).toFixed(2)}deg`);
          this.hero.style.setProperty('--spot-x', `${((x + 1) * 50).toFixed(1)}%`);
          this.hero.style.setProperty('--spot-y', `${((y + 1) * 50).toFixed(1)}%`);
        });

        this.onMove = (event) => {
          const rect = this.hero.getBoundingClientRect();
          move(((event.clientX - rect.left) / rect.width) * 2 - 1, ((event.clientY - rect.top) / rect.height) * 2 - 1);
        };
        this.onLeave = () => move(0, 0);
        this.hero.addEventListener('pointermove', this.onMove, { passive: true });
        this.hero.addEventListener('pointerleave', this.onLeave);

        this.querySelectorAll('.tcgz-hand__card').forEach((card) => tcgzBindGlare(card));
      }

      disconnectedCallback() {
        this.hero?.removeEventListener('pointermove', this.onMove);
        this.hero?.removeEventListener('pointerleave', this.onLeave);
      }
    }
  );
}

/* Moves the foil highlight to wherever the pointer is on a card. */
function tcgzBindGlare(el, tiltTarget) {
  if (el.dataset.tcgzGlare) return;
  el.dataset.tcgzGlare = '1';
  const tilt = tiltTarget || null;
  const follow = tcgzFollow((x, y) => {
    el.style.setProperty('--mx', `${((x + 1) * 50).toFixed(1)}%`);
    el.style.setProperty('--my', `${((y + 1) * 50).toFixed(1)}%`);
    if (tilt) {
      tilt.style.setProperty('--rx', `${(-y * 6).toFixed(2)}deg`);
      tilt.style.setProperty('--ry', `${(x * 6).toFixed(2)}deg`);
    }
  }, 0.18);
  el.addEventListener(
    'pointermove',
    (event) => {
      const rect = el.getBoundingClientRect();
      follow(((event.clientX - rect.left) / rect.width) * 2 - 1, ((event.clientY - rect.top) / rect.height) * 2 - 1);
    },
    { passive: true }
  );
  el.addEventListener('pointerleave', () => follow(0, 0));
}

/* ---------- Product cards: tilt + foil on pointer ---------- */

function tcgzBindCards(root = document) {
  if (!tcgzFinePointer.matches || tcgzReducedMotion.matches) return;
  root.querySelectorAll('.card-wrapper .card--card').forEach((card) => tcgzBindGlare(card, card));
  root.querySelectorAll('.tcgz-game__link').forEach((tile) => tcgzBindGlare(tile, tile));
}

/* ---------- Product rail ---------- */

if (!customElements.get('tcgz-rail')) {
  customElements.define(
    'tcgz-rail',
    class TcgzRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.tcgz-rail__track');
        if (!this.track) return;
        this.prev = document.querySelector(`[data-rail-prev][aria-controls="${this.track.id}"]`);
        this.next = document.querySelector(`[data-rail-next][aria-controls="${this.track.id}"]`);
        this.onScroll = this.update.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', () => this.step(-1));
        this.next?.addEventListener('click', () => this.step(1));
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);
        this.update();
        tcgzBindCards(this);
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      step(direction) {
        const item = this.track.querySelector('.tcgz-rail__item');
        if (!item) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / (item.offsetWidth + gap)));
        this.track.scrollBy({
          left: direction * perPage * (item.offsetWidth + gap),
          behavior: tcgzReducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      }
    }
  );
}

/* ---------- One-time reveals: rails deal their cards, chat bubbles arrive ---------- */

(() => {
  const targets = document.querySelectorAll('.tcgz-rail__track, .tcgz-telegram__chat');
  const show = (el) => el.classList.add('is-visible');
  if (!('IntersectionObserver' in window)) {
    targets.forEach(show);
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.05, rootMargin: '0px 0px -5% 0px' }
  );
  targets.forEach((el) => observer.observe(el));
})();

/* ---------- Game ticker: auto-scrolls, swipeable on touch, draggable with the mouse ---------- */
/* The section is a scroll container holding the list twice; scrollLeft wraps by one list
   width so the loop is seamless in both directions. */
document.querySelectorAll('.tcgz-marquee').forEach((scroller) => {
  const track = scroller.querySelector('.tcgz-marquee__track');
  if (!track) return;
  const viewport = scroller.querySelector('.tcgz-marquee__viewport');
  const duration = parseFloat(getComputedStyle(viewport).getPropertyValue('--duration')) || 45;
  let paused = false;
  let resumeTimer = null;
  let last = performance.now();
  let pos = 0;

  const wrap = () => {
    const w = track.offsetWidth;
    if (!w) return;
    if (scroller.scrollLeft >= w) scroller.scrollLeft -= w;
    else if (scroller.scrollLeft <= 0) scroller.scrollLeft += w;
  };
  const pause = () => {
    paused = true;
    clearTimeout(resumeTimer);
  };
  const resumeSoon = () => {
    clearTimeout(resumeTimer);
    resumeTimer = setTimeout(() => {
      paused = false;
      pos = scroller.scrollLeft;
    }, 1500);
  };

  // start one list in, so dragging backwards also has content
  requestAnimationFrame(() => {
    scroller.scrollLeft = track.offsetWidth;
    pos = scroller.scrollLeft;
  });

  const tick = (now) => {
    const dt = Math.min(now - last, 64);
    last = now;
    if (!paused && !tcgzReducedMotion.matches) {
      pos += (track.offsetWidth / duration) * (dt / 1000);
      scroller.scrollLeft = pos;
      wrap();
      pos = scroller.scrollLeft + (pos % 1);
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  scroller.addEventListener('scroll', () => paused && wrap(), { passive: true });
  scroller.addEventListener('mouseenter', pause);
  scroller.addEventListener('mouseleave', resumeSoon);
  scroller.addEventListener('focusin', pause);
  scroller.addEventListener('focusout', resumeSoon);
  scroller.addEventListener('touchstart', pause, { passive: true });
  scroller.addEventListener('touchend', resumeSoon, { passive: true });

  // mouse drag (touch scrolls natively)
  let dragStartX = 0;
  let dragStartScroll = 0;
  let dragged = false;
  scroller.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    pause();
    dragStartX = event.clientX;
    dragStartScroll = scroller.scrollLeft;
    dragged = false;
    scroller.classList.add('is-dragging');
    const move = (e) => {
      const dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 5) dragged = true;
      scroller.scrollLeft = dragStartScroll - dx;
      wrap();
      if (scroller.scrollLeft !== dragStartScroll - dx) dragStartScroll = scroller.scrollLeft + dx;
    };
    const up = () => {
      scroller.classList.remove('is-dragging');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!scroller.matches(':hover')) resumeSoon();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  });
  // a drag must not count as a click on the game name under the pointer
  scroller.addEventListener(
    'click',
    (event) => {
      if (dragged) {
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      }
    },
    true
  );
  scroller.addEventListener('dragstart', (event) => event.preventDefault());
});

/* ---------- Hero news pill: rotates through the merchant's news lines ---------- */
document.querySelectorAll('[data-tcgz-news]').forEach((pill) => {
  let items;
  try {
    items = JSON.parse(pill.dataset.tcgzNews);
  } catch (e) {
    return;
  }
  if (!Array.isArray(items) || items.length < 2) return;
  const text = pill.querySelector('.tcgz-hero__promo-text');
  const fallbackHref = pill.getAttribute('href');
  let index = 0;
  let hovering = false;
  pill.addEventListener('mouseenter', () => (hovering = true));
  pill.addEventListener('mouseleave', () => (hovering = false));
  pill.addEventListener('focusin', () => (hovering = true));
  pill.addEventListener('focusout', () => (hovering = false));

  setInterval(() => {
    if (hovering || document.hidden) return;
    index = (index + 1) % items.length;
    const next = items[index];
    const swap = () => {
      text.textContent = next.t;
      pill.setAttribute('href', next.l || fallbackHref);
    };
    if (tcgzReducedMotion.matches) return swap();
    text.classList.add('is-leaving');
    setTimeout(() => {
      swap();
      text.classList.remove('is-leaving');
      text.classList.add('is-entering');
      requestAnimationFrame(() => requestAnimationFrame(() => text.classList.remove('is-entering')));
    }, 220);
  }, 4000);
});

/* ---------- Mobile: search becomes a floating button with a bottom sheet ---------- */
/* The header uses backdrop-filter, which traps position:fixed children, so the mobile
   search (the copy Dawn renders directly in .header) is moved to <body>. */
(() => {
  const search = document.querySelector('.section-header .header > .header__search');
  if (!search) return;
  search.classList.add('tcgz-fab-search');
  document.body.appendChild(search);
})();

/* ---------- Mobile: cart becomes a floating button above the search one ---------- */
/* Moved (not cloned) so cart-drawer.js listeners and the #cart-icon-bubble re-render keep working;
   a placeholder lets it return to the header on wider screens. */
(() => {
  const cart = document.getElementById('cart-icon-bubble');
  if (!cart) return;
  const home = document.createComment('tcgz-cart-home');
  cart.before(home);
  const mobile = window.matchMedia('(max-width: 989px)');
  const place = () => {
    if (mobile.matches) {
      cart.classList.add('tcgz-fab-cart');
      document.body.appendChild(cart);
    } else {
      cart.classList.remove('tcgz-fab-cart');
      home.after(cart);
    }
  };
  place();
  mobile.addEventListener('change', place);
})();

tcgzBindCards();
document.addEventListener('shopify:section:load', (event) => tcgzBindCards(event.target));
